
import { from, of as observableOf, Subject } from 'rxjs'

import ContentLayout from 'components/ContentLayout'
import { FetchFeed } from 'components/Fetch'
import SimpleLoader from 'components/SimpleLoader'
import { Feed, FEED_TYPE_KEYWORD } from 'interfaces'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { useDispatch } from 'react-redux'
import { useLocation, useNavigate, useParams } from 'react-router'
import { catchError, distinctUntilChanged, mergeMap, switchMap } from 'rxjs/operators'
import { createFeed, getOriginalFeed } from 'services/content/feeds/actions'
import { addRecentlyViewed } from 'services/search/actions'
import { message } from 'services/snackbar'
import { StoreThunkDispatch } from 'store/state'
import { resetScroll } from 'utils/dom'
import { ADD_FEED, updateState } from '../state/actions'
import { SearchContext } from '../state/context'
import styles from './SearchRoot.pcss'
import { FilterType } from 'shared'

export function SearchFeedRoute() {
  const intl = useIntl()
  const location = useLocation()
  const params = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [state, searchDispatch] = useContext(SearchContext)
  const [feed, setFeed] = useState<Feed | null>(null)
  const [loading, setLoading] = useState(false)
  const [filter, setFilter] = useState<FilterType | undefined>(location.state?.filter as FilterType || undefined)
  const scrollElementRef = useRef<HTMLElement | undefined>(undefined)
  const scrollConfig = scrollElementRef.current ? { element: scrollElementRef.current } : undefined

  const queryRef = useRef<string>(state.query)
  const create$ = useRef<Subject<string>>(undefined)

  useEffect(() => {
    create$.current = new Subject()
    create$.current.pipe(
      distinctUntilChanged(),
      switchMap(name => {
        setLoading(true)
        return from(dispatch(createFeed({ handle: name, type: FEED_TYPE_KEYWORD })).unwrap())
          .pipe(
            mergeMap((response: any) => from(dispatch(getOriginalFeed(response.originalSource)).unwrap())),
            catchError(() => {
              return observableOf({ error: true })
            })
          )
      }))
      .subscribe((response: any) => {
        setLoading(false)
        if (response.error) {
          dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
        } else {
          setFeed(response)
          searchDispatch(updateState({ activeFeed: response }))
          const redirectUrl = location.state?.backUrl || `/content/search/sources?q=${queryRef.current}`
          navigate(redirectUrl, { state: { backUrl: location.state?.backUrl } })
        }
      })

    return () => create$.current?.unsubscribe()
  }, [dispatch, location.state?.backUrl, navigate, intl, searchDispatch])

  useEffect(() => {
    scrollElementRef.current = document.querySelector('[data-test="main"]') as HTMLElement
    resetScroll()
  }, [])

  const onQueryChange = useCallback((next: string) => {
    if (!loading && feed && feed.name !== next) {
      const cachedFeed = state.createdKeywordFeeds.find(f => f.name === next)
      if (cachedFeed) {
        setFeed(cachedFeed)
      } else {
        setFeed(null)
        searchDispatch(updateState({ activeFeed: undefined }))
        create$.current?.next(queryRef.current)
      }
    }
  }, [loading, feed, state.createdKeywordFeeds, searchDispatch])

  useEffect(() => {
    if (queryRef.current !== state.query) {
      queryRef.current = state.query
      onQueryChange(state.query)
    }
  }, [state.query, onQueryChange])

  useEffect(() => {
    if (feed?.id) {
      dispatch(addRecentlyViewed({ source: feed, type: 'feed' }))
    }
  }, [dispatch, feed])

  useEffect(() => {
    if (!feed && state.activeFeed?.id === params.id) {
      setFeed(state.activeFeed || null)
    }
  }, [params.id, state.activeFeed, feed])

  const trackFeedSaved = useCallback((feed: Feed) => {
    searchDispatch({ type: ADD_FEED, payload: feed })
    return true
  }, [searchDispatch])

  const onFetched = useCallback((f: Feed) => {
    searchDispatch(updateState({ createdKeywordFeeds: [...state.createdKeywordFeeds, f] }))
    setFeed(f)
  }, [searchDispatch, state.createdKeywordFeeds])

  const onFetchFailed = useCallback(() => {
    dispatch(message(intl.formatMessage({ id: 'errors.generic-support' }), 'error'))
  }, [dispatch, intl])

  const goBack = useCallback(() => {
    if (location.state?.backUrl) {
      navigate(`${location.state.backUrl}${location.search}`, { state: { filter } })
    } else {
      navigate(`/content/search/sources/${feed?.type || ''}?q=${state.query}`)
    }
  }, [location.state?.backUrl, location.search, navigate, filter, feed?.type, state.query])

  if (!feed) {
    return (
      <div>
        {!loading && <FetchFeed id={params.id as string} onFetched={onFetched} onFailed={onFetchFailed} />}
        <SimpleLoader small />
      </div>
    )
  }

  return (
    <div className={styles['grid-wrapper']}>
      <ContentLayout
        source={feed}
        expanded
        filtersBoxClassName={styles['grid-filters']}
        filter={filter}
        onCollapsed={goBack}
        onSaveSource={trackFeedSaved}
        onFilterChanged={setFilter}
        scroll={scrollConfig}
      />
    </div>
  )
}

export default SearchFeedRoute
